import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Download, Mail, Linkedin, FileText, GraduationCap, BookOpen, Award, Briefcase, Building2, Mic2, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ProfilePhoto } from "@/components/site/ProfilePhoto";
import { BannerPhoto } from "@/components/site/BannerPhoto";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal, Stagger, staggerItem } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/SectionLabel";
import { getSection, listItems } from "@/lib/api";

export default function HomePage() {
    const [home, setHome] = useState(null);
    const [research, setResearch] = useState(null);
    const [about, setAbout] = useState(null);
    const [teaching, setTeaching] = useState(null);
    const [contact, setContact] = useState(null);
    const [publications, setPublications] = useState([]);
    const [awards, setAwards] = useState([]);
    const [courses, setCourses] = useState([]);
    const [conferences, setConferences] = useState([]);

    useEffect(() => {
        getSection("home").then((r) => setHome(r.data || {}));
        getSection("research").then((r) => setResearch(r.data || {}));
        getSection("about").then((r) => setAbout(r.data || {}));
        getSection("teaching").then((r) => setTeaching(r.data || {}));
        getSection("contact").then((r) => setContact(r.data || {}));
        listItems("publications").then(setPublications);
        listItems("awards").then(setAwards);
        listItems("courses").then(setCourses);
        listItems("conferences").then(setConferences);
    }, []);

    const h = home || {};
    const r = research || {};
    const a = about || {};
    const t = teaching || {};
    const c = contact || {};

    const published = publications.filter((p) => (p.status || "published") === "published").slice(0, 3);
    const forthcoming = publications.filter((p) => p.status === "forthcoming");
    const underReview = publications.filter((p) => p.status === "under_review");
    const presented = conferences.filter((cf) => (cf.type || "presented") === "presented");
    const summaryParagraphs = (a.summary || "").split(/\n\n+/).slice(0, 1);
    const stints = a.industry_stints || [];

    return (
        <>
            <PageMeta
                title="Aditya Baheti — Academic Fellow · Corporate & Securities Law"
                description="Academic Fellow at National Law University, Jodhpur — researching company law, corporate governance, securities regulation, and law & emerging technology."
                path="/"
            />

            {/* ============================ HERO =============================== */}
            <section
                className="relative overflow-hidden"
                data-testid="home-hero"
            >
                {/* Full-bleed profile photo background */}
                <div className="absolute inset-0">
                    <ProfilePhoto
                        className="h-full w-full object-cover object-center"
                        alt="Aditya Baheti — Academic Fellow"
                    />
                </div>
                {/* Dark gradient overlay for legibility */}
                <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"
                />
                <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
                />

                {/* Overlaid content */}
                <div className="relative min-h-[560px] sm:min-h-[640px] lg:min-h-[calc(100vh-0px)] flex items-end">
                    <div className="w-full max-w-4xl mx-auto px-6 sm:px-10 lg:px-14 pb-16 sm:pb-20 lg:pb-24">
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/80"
                        >
                            {h.currently || "National Law University, Jodhpur"}
                        </motion.p>
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-4 font-display text-white text-[clamp(2.75rem,7vw,6rem)] leading-[0.95] tracking-[-0.02em]"
                        >
                            {h.name || "Aditya Baheti"}
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-5 font-display text-xl sm:text-2xl lg:text-3xl leading-snug text-white/95 max-w-3xl"
                        >
                            {h.role || "Academic Fellow · Corporate & Securities Law"}
                        </motion.p>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-6 max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed"
                        >
                            {h.identity}
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.32, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 flex flex-wrap items-center gap-3"
                        >
                            <Link
                                to="/research"
                                data-testid="home-hero-primary-cta-button"
                                className="inline-flex items-center gap-2 h-11 px-6 rounded-full border-[1.5px] border-white text-white font-medium text-sm tracking-wide hover:bg-white hover:text-foreground transition-colors"
                            >
                                View Research <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link
                                to="/cv"
                                data-testid="home-hero-secondary-cta-button"
                                className="inline-flex items-center gap-2 h-11 px-6 rounded-full border-[1.5px] border-white/70 text-white font-medium text-sm tracking-wide hover:border-white hover:bg-white/10 transition-colors"
                            >
                                <Download className="h-4 w-4" /> Curriculum Vitæ
                            </Link>
                            <div className="flex items-center gap-1 ml-1">
                                <IconLink href={`mailto:${(c.emails || [])[0] || "adibaheti07@gmail.com"}`} label="Email" testid="home-icon-email">
                                    <Mail className="h-4 w-4" />
                                </IconLink>
                                {c.linkedin && <IconLink href={c.linkedin} label="LinkedIn" testid="home-icon-linkedin"><Linkedin className="h-4 w-4" /></IconLink>}
                                {c.orcid && <IconLink href={c.orcid} label="ORCID" testid="home-icon-orcid"><Fingerprint className="h-4 w-4" /></IconLink>}
                                {c.ssrn && <IconLink href={c.ssrn} label="SSRN" testid="home-icon-ssrn"><FileText className="h-4 w-4" /></IconLink>}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8"
                        >
                            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/70 mb-3">Qualifications</p>
                            <div className="flex flex-wrap gap-2" data-testid="home-qualifications-block">
                                {(h.qualifications || ["LL.M. (Gold Medalist)", "LL.B.", "CS", "B.Com", "UGC NET (JRF)"]).map((q) => (
                                    <span
                                        key={q}
                                        data-testid={`home-qualification-${q}`}
                                        className="inline-flex items-center rounded-full border border-white/50 bg-white/10 backdrop-blur-sm text-white/95 font-mono text-[11px] tracking-wide px-3 py-1"
                                    >
                                        {q}
                                    </span>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ============================ ABOUT SUMMARY ==================== */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <SectionLabel label="About" />
                        <Reveal>
                            <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">A career at the seam of practice and scholarship.</h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <div className="space-y-4 text-base sm:text-lg leading-relaxed text-foreground/85">
                            {summaryParagraphs.map((p, i) => (
                                <Reveal key={i} delay={i * 0.05}>
                                    <p>{p}</p>
                                </Reveal>
                            ))}
                        </div>
                        <Reveal delay={0.1}>
                            <div className="mt-6">
                                <Button asChild variant="ghost" data-testid="home-about-cta">
                                    <Link to="/about">Read the full biography <ArrowRight className="ml-2 h-4 w-4" /></Link>
                                </Button>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ============================ ACADEMIC ========================== */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 brass-glow" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12">
                        <div className="lg:col-span-4">
                            <SectionLabel label="Academic" />
                            <Reveal>
                                <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">In the academy.</h2>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <p className="mt-4 text-muted-foreground leading-relaxed max-w-[46ch]">Currently an Academic Fellow at NLU Jodhpur — teaching corporate and securities law, and publishing across peer-reviewed journals and blogs.</p>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <Stagger className="grid sm:grid-cols-2 gap-3">
                                <AcademicStat icon={Building2} kicker="Position" value="Academic Fellow" detail="National Law University, Jodhpur" tone="oxblood" />
                                <AcademicStat icon={GraduationCap} kicker="Highest degree" value="LL.M. (Gold Medalist)" detail="First rank; highest marks in dissertation" tone="brass" />
                                <AcademicStat icon={BookOpen} kicker="Publications" value={`${publications.length} papers`} detail={`${published.length} published · ${forthcoming.length} forthcoming · ${underReview.length} under review`} tone="ocean" />
                                <AcademicStat icon={Mic2} kicker="Conferences" value={`${conferences.length} events`} detail={`${presented.length} paper${presented.length === 1 ? "" : "s"} presented · ${conferences.length - presented.length} attended`} tone="moss" />
                            </Stagger>
                        </div>
                    </div>

                    {/* Courses summary — degree level only */}
                    {courses.length > 0 && (
                        <div className="mt-6 grid lg:grid-cols-12 gap-10">
                            <div className="lg:col-span-4">
                                <SectionLabel label="Courses taught" />
                                <Reveal>
                                    <p className="mt-2 text-muted-foreground leading-relaxed max-w-[46ch]">Presidency University · LL.B. Detailed subject list on the Teaching page.</p>
                                </Reveal>
                                <Reveal delay={0.05}>
                                    <div className="mt-5">
                                        <Button asChild variant="ghost" data-testid="home-teaching-cta">
                                            <Link to="/teaching">Full teaching page <ArrowRight className="ml-2 h-4 w-4" /></Link>
                                        </Button>
                                    </div>
                                </Reveal>
                            </div>
                            <div className="lg:col-span-8">
                                <Reveal>
                                    <Card className="p-8 rounded-2xl border-border/70 bg-card">
                                        <div className="flex items-center gap-3">
                                            <div className="h-11 w-11 rounded-xl bg-forest/10 grid place-items-center">
                                                <GraduationCap className="h-5 w-5 text-forest" />
                                            </div>
                                            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-forest">Degree level</span>
                                        </div>
                                        <p className="mt-4 font-display text-4xl sm:text-5xl leading-tight">LL.B.</p>
                                        <p className="mt-3 text-muted-foreground max-w-[62ch] leading-relaxed">{courses.length} courses at Presidency University — teaching subjects including Securities Regulation, Mergers & Acquisitions, and Corporate Governance at the undergraduate law level.</p>
                                    </Card>
                                </Reveal>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* ============================ RESEARCH AREAS =================== */}
            <section className="relative bg-secondary/40 border-y border-border/70">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <SectionLabel label="Research" />
                            <Reveal>
                                <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">Four questions that shape my work.</h2>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <p className="mt-4 text-muted-foreground leading-relaxed max-w-[46ch]">{r.vision || r.agenda}</p>
                            </Reveal>
                            <Reveal delay={0.1}>
                                <div className="mt-6">
                                    <Button asChild variant="ghost" data-testid="home-research-cta">
                                        <Link to="/research">Full research page <ArrowRight className="ml-2 h-4 w-4" /></Link>
                                    </Button>
                                </div>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <Stagger className="grid sm:grid-cols-2 gap-3">
                                {(r.areas || r.interests || []).map((area) => (
                                    <motion.div key={area} variants={staggerItem}>
                                        <Card className="h-full p-5 rounded-2xl border-border/70 bg-card">
                                            <BookOpen className="h-4 w-4 text-oxblood" />
                                            <p className="mt-3 font-display text-2xl leading-tight">{area}</p>
                                        </Card>
                                    </motion.div>
                                ))}
                            </Stagger>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================ INDUSTRY EXPOSURE ================ */}
            {stints.length > 0 && (
                <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <div className="grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-4">
                            <SectionLabel label="Industry exposure" />
                            <Reveal>
                                <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">Practice-informed scholarship.</h2>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <p className="mt-4 text-muted-foreground leading-relaxed max-w-[46ch]">Internships and in-house experience across leading Indian law firms and a listed NBFC — grounding theory in contemporary corporate practice.</p>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {stints.map((s) => (
                                    <motion.div key={s} variants={staggerItem}>
                                        <Card className="h-full p-4 rounded-2xl border-border/70 bg-card flex items-center gap-3">
                                            <div className="h-9 w-9 rounded-lg bg-oxblood/10 grid place-items-center shrink-0">
                                                <Briefcase className="h-4 w-4 text-oxblood" />
                                            </div>
                                            <p className="font-display text-lg leading-snug">{s}</p>
                                        </Card>
                                    </motion.div>
                                ))}
                            </Stagger>
                        </div>
                    </div>
                </section>
            )}

            {/* ============================ RECENT PUBLICATIONS ============== */}
            {published.length > 0 && (
                <section className="bg-secondary/40 border-y border-border/70">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                            <div>
                                <SectionLabel label="Selected publications" />
                                <Reveal><h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight max-w-3xl">Recently in print.</h2></Reveal>
                            </div>
                            <Reveal delay={0.05}>
                                <Button asChild variant="ghost" data-testid="home-view-all-publications-button"><Link to="/research">All publications <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
                            </Reveal>
                        </div>
                        <div className="divide-y divide-border/70 border-y border-border/70 bg-card/50 rounded-2xl px-6">
                            {published.map((p, i) => (
                                <Reveal key={p.id} delay={i * 0.05}>
                                    <Link to="/research" className="group grid md:grid-cols-12 gap-4 py-6 items-start" data-testid={`home-publication-${i}`}>
                                        <div className="md:col-span-2 font-mono text-[11px] uppercase tracking-widest text-oxblood">Published{p.year ? ` · ${p.year}` : ""}</div>
                                        <div className="md:col-span-10">
                                            <p className="font-display text-2xl leading-snug group-hover:underline underline-offset-4 decoration-1">{p.title}</p>
                                            <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{p.venue}{p.venue_detail ? ` · ${p.venue_detail}` : ""}</p>
                                        </div>
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ============================ CONFERENCE PRESENTATIONS ========= */}
            {/* Speaking section trimmed from home — visible on Research page */}

            {/* ============================ AWARDS =========================== */}
            {awards.length > 0 && (
                <section className="bg-secondary/40 border-y border-border/70">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                        <div className="grid lg:grid-cols-12 gap-10">
                            <div className="lg:col-span-4">
                                <SectionLabel label="Recognition" />
                                <Reveal>
                                    <h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">Awards & achievements.</h2>
                                </Reveal>
                            </div>
                            <div className="lg:col-span-8">
                                <Stagger className="space-y-3">
                                    {awards.map((aw) => (
                                        <motion.div key={aw.id} variants={staggerItem}>
                                            <Card className="p-5 rounded-2xl border-border/70 bg-card flex items-start gap-4">
                                                <div className="h-10 w-10 rounded-full bg-brass/15 grid place-items-center shrink-0">
                                                    <Award className="h-5 w-5 text-brass" />
                                                </div>
                                                <div>
                                                    <p className="font-display text-xl leading-snug">{aw.title}</p>
                                                    {aw.issuer && <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{aw.issuer}{aw.year ? ` · ${aw.year}` : ""}</p>}
                                                    {aw.description && <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{aw.description}</p>}
                                                </div>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </Stagger>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* ============================ VOLUNTEERING ===================== */}
            {/* Trimmed from home — visible on About page */}

            {/* ============================ TEACHING QUOTE =================== */}
            <section className="bg-secondary/40 border-y border-border/70">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                    <div className="grid lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-4">
                            <SectionLabel label="Teaching" />
                            <Reveal><h2 className="font-display text-3xl sm:text-4xl leading-[1.1] tracking-tight">Teaching as translation.</h2></Reveal>
                        </div>
                        <div className="lg:col-span-8">
                            <Reveal>
                                <blockquote className="pl-6 border-l-2 border-oxblood">
                                    <GraduationCap className="h-6 w-6 text-brass mb-3" />
                                    <p className="font-display text-2xl sm:text-3xl leading-snug tracking-tight">
                                        “{t.philosophy || "I approach teaching as a dialogue — rigour, humility, and constant translation between doctrine and practice."}”
                                    </p>
                                    <p className="mt-5 text-sm text-muted-foreground">
                                        <Link to="/teaching" className="link-underline">Read the full teaching page <ArrowRight className="inline h-3 w-3" /></Link>
                                    </p>
                                </blockquote>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* ============================ CONTACT CTA ====================== */}
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <Reveal>
                    <div className="relative overflow-hidden rounded-3xl border border-border/70 bg-card p-8 sm:p-14 hero-glow">
                        <div className="grid md:grid-cols-3 gap-8 items-center">
                            <div className="md:col-span-2">
                                <SectionLabel label="Contact" />
                                <h3 className="font-display text-3xl sm:text-5xl leading-[1.05] tracking-tight">Collaborate, write, or teach together.</h3>
                                <p className="mt-4 text-muted-foreground max-w-[54ch] text-base sm:text-lg leading-relaxed">Open to research collaborations, teaching invitations, guest lectures, and thoughtful conversations.</p>
                            </div>
                            <div className="flex md:justify-end">
                                <Link
                                    to="/contact"
                                    className="btn-outline-pill accent"
                                    data-testid="home-cta-contact-button"
                                >
                                    <Mail className="h-4 w-4" /> Get in touch
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>
        </>
    );
}

function AcademicStat({ icon: Icon, kicker, value, detail, tone = "oxblood" }) {
    const toneClasses = {
        oxblood: "bg-oxblood/10 text-oxblood",
        brass: "bg-brass/15 text-brass",
        ocean: "bg-[hsl(var(--ocean)/0.10)] text-[hsl(var(--ocean))]",
        moss: "bg-moss/15 text-moss",
    };
    return (
        <motion.div variants={staggerItem}>
            <Card className="h-full p-5 rounded-2xl border-border/70 bg-card">
                <div className="flex items-center gap-3">
                    <div className={`h-9 w-9 rounded-lg grid place-items-center ${toneClasses[tone] || toneClasses.oxblood}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{kicker}</p>
                </div>
                <p className="mt-3 font-display text-2xl leading-tight">{value}</p>
                {detail && <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{detail}</p>}
            </Card>
        </motion.div>
    );
}

function IconLink({ href, label, children, testid }) {
    return (
        <a
            href={href}
            target={href?.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer noopener"
            title={label}
            aria-label={label}
            data-testid={testid}
            className="h-9 w-9 rounded-full border border-white/50 grid place-items-center text-white/85 hover:text-white hover:bg-white/10 transition-colors"
        >
            {children}
        </a>
    );
}

function Fingerprint(props) {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
            <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4" />
            <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
            <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
            <path d="M2 12a10 10 0 0 1 18-6" />
            <path d="M2 16h.01" />
            <path d="M21.8 16c.2-2 .131-5.354 0-6" />
            <path d="M5 19.5C5.5 18 6 15 6 12a6 6 0 0 1 .34-2" />
            <path d="M8.65 22c.21-.66.45-1.32.57-2" />
            <path d="M9 6.8a6 6 0 0 1 9 5.2v2" />
        </svg>
    );
}
